import { useEffect } from 'react';

/**
 * `esc` steps out of a takeover surface — but only when nothing closer owns
 * the key. Fields, dialogs, dropdowns, and anything that already called
 * `preventDefault` win; dismissing a layer must never also exit the surface.
 *
 * `enabled` lets a surface suspend the behavior on routes where `esc` has a
 * local meaning (e.g. deselecting nodes on the automation editor canvas).
 */
export function useTakeoverEscape(onEscape: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const target = e.target as HTMLElement | null;
      // `.react-flow`: xyflow deselects nodes/edges on Escape without calling
      // preventDefault, so a press with focus in a graph canvas (automation
      // run pages, the editor) must deselect only — never also exit.
      //
      // Plain inputs / textareas / selects / dialogs / menus / listboxes all
      // claim `esc` for their own close/blur behavior, so a press landing on
      // one of them is left to that element. (A `contenteditable` is NOT
      // included: rich-text composers such as the chat composer don't have a
      // default `esc` action, and including the selector would swallow the
      // key on the Labs chat page where the composer is autofocus-mounted
      // — `esc`-back would never fire. A contenteditable that DOES want to
      // own `esc` can still `preventDefault()` or `stopPropagation()` on it;
      // the `defaultPrevented` guard above and the document-level listener
      // pick that up.)
      if (
        target?.closest(
          'input, textarea, select, [role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], .react-flow',
        )
      ) {
        return;
      }
      if (
        document.querySelector(
          '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]',
        )
      ) {
        return;
      }
      onEscape();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onEscape, enabled]);
}
